import { useEffect, useState, type FormEvent } from 'react';
import { apiGet, apiPatch, type BusinessInfo as BizInfo } from '~/lib/api';

export function BusinessInfo() {
  const [info, setInfo] = useState<BizInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    apiGet<{ business_info: BizInfo }>('/api/admin/business-info')
      .then((data) => setInfo(data.business_info))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  function patch<K extends keyof BizInfo>(key: K, value: BizInfo[K]) {
    setInfo((i) => (i ? { ...i, [key]: value } : i));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!info) return;
    setSaving(true);
    setError(null);
    const { id, updated_at, ...patchData } = info;
    try {
      await apiPatch('/api/admin/business-info', patchData);
      setSavedAt(new Date());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="trc-page"><p>Loading…</p></div>;
  if (!info) return <div className="trc-page"><p className="trc-error">{error}</p></div>;

  return (
    <div className="trc-page">
      <header className="trc-page-header">
        <p className="trc-eyebrow">Settings</p>
        <h1 className="trc-osw trc-page-title">Business Info</h1>
      </header>
      <p className="trc-page-empty">
        These fields feed the <code>LocalBusiness</code> schema.org markup on every public page,
        plus the site footer and contact pages.
      </p>

      <form onSubmit={handleSubmit} className="trc-form">
        <fieldset className="trc-fieldset">
          <legend>Identity</legend>
          <div className="trc-field">
            <label htmlFor="biz-name">Business name</label>
            <input id="biz-name" value={info.business_name} onChange={(e) => patch('business_name', e.target.value)} required />
          </div>
          <div className="trc-field">
            <label htmlFor="tagline">Tagline</label>
            <input id="tagline" value={info.tagline ?? ''} onChange={(e) => patch('tagline', e.target.value || null)} />
          </div>
        </fieldset>

        <fieldset className="trc-fieldset">
          <legend>Contact</legend>
          <div className="trc-field-row">
            <div className="trc-field">
              <label htmlFor="phone">Phone</label>
              <input id="phone" type="tel" value={info.phone ?? ''} onChange={(e) => patch('phone', e.target.value || null)} />
            </div>
            <div className="trc-field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={info.email ?? ''} onChange={(e) => patch('email', e.target.value || null)} />
            </div>
          </div>
        </fieldset>

        <fieldset className="trc-fieldset">
          <legend>Address</legend>
          <div className="trc-field">
            <label htmlFor="street">Street</label>
            <input id="street" value={info.address_street ?? ''} onChange={(e) => patch('address_street', e.target.value || null)} />
          </div>
          <div className="trc-field-row">
            <div className="trc-field">
              <label htmlFor="city">City</label>
              <input id="city" value={info.address_city ?? ''} onChange={(e) => patch('address_city', e.target.value || null)} />
            </div>
            <div className="trc-field">
              <label htmlFor="state">State</label>
              <input id="state" value={info.address_state ?? ''} onChange={(e) => patch('address_state', e.target.value || null)} />
            </div>
            <div className="trc-field">
              <label htmlFor="zip">ZIP</label>
              <input id="zip" value={info.address_zip ?? ''} onChange={(e) => patch('address_zip', e.target.value || null)} />
            </div>
          </div>
          <div className="trc-field-row">
            <div className="trc-field">
              <label htmlFor="lat">Latitude</label>
              <input id="lat" type="number" step="0.0000001" value={info.latitude ?? ''}
                onChange={(e) => patch('latitude', e.target.value ? parseFloat(e.target.value) : null)} />
            </div>
            <div className="trc-field">
              <label htmlFor="lng">Longitude</label>
              <input id="lng" type="number" step="0.0000001" value={info.longitude ?? ''}
                onChange={(e) => patch('longitude', e.target.value ? parseFloat(e.target.value) : null)} />
            </div>
          </div>
        </fieldset>

        <fieldset className="trc-fieldset">
          <legend>Social & external profiles (for schema sameAs)</legend>
          <div className="trc-field">
            <label htmlFor="fb">Facebook URL</label>
            <input id="fb" value={info.facebook ?? ''} onChange={(e) => patch('facebook', e.target.value || null)} />
          </div>
          <div className="trc-field">
            <label htmlFor="ig">Instagram URL</label>
            <input id="ig" value={info.instagram ?? ''} onChange={(e) => patch('instagram', e.target.value || null)} />
          </div>
          <div className="trc-field">
            <label htmlFor="ta">TripAdvisor URL</label>
            <input id="ta" value={info.tripadvisor ?? ''} onChange={(e) => patch('tripadvisor', e.target.value || null)} />
          </div>
          <div className="trc-field">
            <label htmlFor="gb">Google Business URL</label>
            <input id="gb" value={info.google_business_url ?? ''} onChange={(e) => patch('google_business_url', e.target.value || null)} />
          </div>
        </fieldset>

        <fieldset className="trc-fieldset">
          <legend>Licenses / Permits</legend>
          <div className="trc-field-row">
            <div className="trc-field">
              <label htmlFor="uscg">USCG license #</label>
              <input id="uscg" value={info.uscg_license_number ?? ''} onChange={(e) => patch('uscg_license_number', e.target.value || null)} />
            </div>
            <div className="trc-field">
              <label htmlFor="adfg">ADF&G permit #</label>
              <input id="adfg" value={info.adfg_permit_number ?? ''} onChange={(e) => patch('adfg_permit_number', e.target.value || null)} />
            </div>
          </div>
        </fieldset>

        {error && <p className="trc-error">Error: {error}</p>}

        <div className="trc-form-actions">
          <button type="submit" disabled={saving} className="trc-button-primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
          {savedAt && <span className="trc-saved-marker">Saved {savedAt.toLocaleTimeString()}</span>}
        </div>
      </form>
    </div>
  );
}
